import type React from 'react';
import {Alert, Box, Skeleton, Stack} from '@mui/material';
import {
    BalanceOutlined, CompareArrowsOutlined, DescriptionOutlined, InfoOutlined, NotificationsNoneOutlined,
    ReceiptLongOutlined, ShowChartOutlined, StorefrontOutlined, TrackChangesOutlined,
} from '@mui/icons-material';
import {generatePath, Outlet, useLocation, useNavigate, useParams, useSearchParams} from 'react-router';
import {PageHeader} from '~/components/main/header/PageHeader.tsx';
import type {BtnGroupTabItem} from '~/components/BtnGroupTabs/BtnGroupTabs';
import {ScrollableTabs} from './components/ScrollableTabs';
import {routes} from '~/routing/routes';
import type {CategoryProfileRouteMatch} from '~/routing/catRoutes';
import {useProfileHeaderQuery} from '../hooks/useCategoryProfileQueries';
import {useMoneyFormat} from '../hooks/useMoneyFormat';
import {CategoryProfileHeader} from './CategoryProfileHeader';
import type {ProfileHeader} from '../types/categoryProfileTypes';
import type {CategoryProfileOutletContext} from './profileTypes';

export const PROFILE_TABS = [
    {title: 'Overview', tab: 'overview', Icon: InfoOutlined},
    {title: 'Strategy', tab: 'strategy', Icon: TrackChangesOutlined},
    {title: 'Suppliers', tab: 'suppliers', Icon: StorefrontOutlined},
    {title: 'Contracts', tab: 'contracts', Icon: DescriptionOutlined},
    {title: 'Transactions', tab: 'transactions', Icon: ReceiptLongOutlined},
    {title: 'Should cost', tab: 'should-cost', Icon: BalanceOutlined},
    {title: 'Market index', tab: 'market-index', Icon: ShowChartOutlined},
    {title: 'Benchmarks', tab: 'benchmarks', Icon: CompareArrowsOutlined},
    {title: 'External signals', tab: 'external-signals', Icon: NotificationsNoneOutlined},
] as const;

/** The count shown in a tab label, where the spec defines one and the header has it. */
function tabCount(tab: string, header: ProfileHeader | undefined): number | null {
    if (!header) return null;
    if (tab === 'suppliers') return header.kpis.suppliers;
    if (tab === 'contracts') return header.kpis.contracts ?? null;
    if (tab === 'transactions') return header.kpis.transactions;
    if (tab === 'external-signals') return header.kpis.externalAlerts || null;
    if (tab === 'should-cost') return header.kpis.shouldCostOpportunities || null;
    return null;
}

/** The record for one category (spec 4): header, URL-synced tabs and the active tab. */
export const CategoryProfileLayout: React.FC = () => {
    const {id} = useParams<CategoryProfileRouteMatch>();
    // Tabs are literal child routes, so the active one is the last path segment, not a route param.
    const tab = useLocation().pathname.replace(/\/$/, '').split('/').pop();
    const databagId = Number(id);
    const [searchParams] = useSearchParams();
    const path = searchParams.get('path') ?? '';
    const from = searchParams.get('from') ?? '';
    const navigate = useNavigate();
    const money = useMoneyFormat();
    const {data: header, isLoading, isError, error} = useProfileHeaderQuery(databagId, path);

    const goTab = (t: string) => navigate({
        pathname: generatePath(routes.category_profile, {id: String(databagId), tab: t}),
        search: searchParams.toString(),
    });
    const back = () => navigate({
        pathname: generatePath(routes.category_explore, {id: String(databagId)}),
        search: from ? `?${new URLSearchParams({path: from})}` : '',
    });
    const openCategory = (p: string) => navigate({
        pathname: generatePath(routes.category_profile, {id: String(databagId), tab: tab || 'overview'}),
        search: `?${new URLSearchParams({path: p, ...(from ? {from} : {})})}`,
    });
    const tabs: BtnGroupTabItem[] = PROFILE_TABS.map(t => {
        const n = tabCount(t.tab, header);
        return {title: n === null ? t.title : `${t.title} (${n.toLocaleString()})`, Icon: t.Icon, onClick: () => goTab(t.tab)};
    });
    const active = tabs[Math.max(0, PROFILE_TABS.findIndex(t => t.tab === tab))].title;

    // Until the profile header loads, the back link is in the app's top bar (Mithra 3.1 header) on its own.
    const backButton = <PageHeader back={{label: 'Explore Categories', onClick: back}}/>;

    if (!path) {
        return <Stack gap={2}>{backButton}<Alert severity="info">Open a category from Explore Categories.</Alert></Stack>;
    }
    if (isError) {
        const status = (error as {response?: {status?: number}})?.response?.status;
        return (
            <Stack gap={2}>
                {backButton}
                <Alert severity={status === 404 ? 'info' : 'error'}>
                    {status === 404 ? 'No profile for this category: it has no spend in this dataset.'
                        : status === 403 ? 'You do not have access to this dataset.'
                            : 'Could not load the category profile.'}
                </Alert>
            </Stack>
        );
    }
    if (isLoading || !header) {
        return <Stack gap={2}>{backButton}<Skeleton height={40} width={300}/><Skeleton variant="rounded" height={300}/></Stack>;
    }

    const context: CategoryProfileOutletContext = {databagId, path, header, money};
    return (
        <Stack gap={2}>
            <CategoryProfileHeader databagId={databagId} header={header} money={money} onBack={back} onOpen={openCategory}/>
            <ScrollableTabs items={tabs} value={active}/>
            <Box sx={{minHeight: 400}}>
                <Outlet context={context}/>
            </Box>
        </Stack>
    );
};
